import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const LtiAssignment = ({ lineItemUrl, backEndUrl, platformUrl }) => {
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = queryParams.get("token");        // used only for Learn API calls; never sent to backend
    const courseIdParam = queryParams.get("courseUuid") || ""; // may be UUID or pk1 like "_6817_1"
    const userIdParam = queryParams.get("uuid") || "";         // may be UUID or pk1 like "_268_1"

    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    // Enrollment state
    const [enrollmentRole, setEnrollmentRole] = useState(""); // "Student", "Instructor", etc.
    const [enrollmentError, setEnrollmentError] = useState("");
    const [loadingEnrollment, setLoadingEnrollment] = useState(false);

    // Students list (for instructors)
    const [students, setStudents] = useState([]); // [{ uuid, name }]
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [studentsError, setStudentsError] = useState("");

    // Grades keyed by student uuid
    const [grades, setGrades] = useState({}); // { "<uuid>": "95" }
    const [rowStatuses, setRowStatuses] = useState({}); // { "<uuid>": { status: "ok"|"error", message?: string } }

    // --- Helpers ---
    const trimBase = (s = "") => s.replace(/\/+$/, "");
    const apiBase = () => trimBase(platformUrl || "");
    const isPk1 = (id = "") => id.startsWith("_");
    const coursePath = (id = "") => (isPk1(id) ? `courses/${id}` : `courses/uuid:${id}`);
    const userPath = (id = "") => (isPk1(id) ? `users/${id}` : `users/uuid:${id}`);

    // Simple in-memory cache for user lookups
    const userCacheRef = useRef(new Map());
    const getCachedUser = (key) => userCacheRef.current.get(key);
    const setCachedUser = (key, val) => userCacheRef.current.set(key, val);

    const fetchUserCanonical = async (idLike) => {
        if (!apiBase() || !token || !idLike) return { uuid: "", name: "" };
        const cacheKey = String(idLike).replace(/^uuid:/i, "");
        const cached = getCachedUser(cacheKey);
        if (cached) return cached;

        const url = `${apiBase()}/learn/api/public/v1/${userPath(idLike)}`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const data = res?.data || {};
        const uuid = (data.uuid || "").toString();
        const name =
            data?.name?.displayName ||
            [data?.name?.given, data?.name?.family].filter(Boolean).join(" ") ||
            data?.userName ||
            uuid;

        const out = { uuid, name };
        setCachedUser(cacheKey, out);
        return out;
    };

    // Fetch my membership
    useEffect(() => {
        const fetchEnrollment = async () => {
            if (!apiBase() || !courseIdParam || !userIdParam || !token) return;
            try {
                setLoadingEnrollment(true);
                setEnrollmentError("");
                const url = `${apiBase()}/learn/api/public/v1/${coursePath(courseIdParam)}/${userPath(userIdParam)}`;
                const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                const role = res?.data?.courseRoleId || res?.data?.courseRole || "";
                setEnrollmentRole(role || "");
            } catch (e) {
                setEnrollmentError(e?.response?.data?.message || e?.message || "Failed to load enrollment.");
            } finally {
                setLoadingEnrollment(false);
            }
        };
        fetchEnrollment();
    }, [platformUrl, courseIdParam, userIdParam, token]);

    // Fetch all students if Instructor
    useEffect(() => {
        const fetchAllStudents = async () => {
            if (!apiBase() || !courseIdParam || !token) return;
            if ((enrollmentRole || "").toLowerCase() !== "instructor") return;
            try {
                setLoadingStudents(true);
                setStudentsError("");
                setStudents([]);
                const base = `${apiBase()}/learn/api/public/v1/${coursePath(courseIdParam)}/users`;
                let url = `${base}?limit=200`;
                const collected = [];
                while (url) {
                    // eslint-disable-next-line no-await-in-loop
                    const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                    const results = res?.data?.results ?? [];
                    for (const m of results) {
                        const role = (m?.courseRoleId || m?.courseRole || "").toLowerCase();
                        if (role === "student") {
                            const memberUserId = m?.userId || m?.user?.id || "";
                            if (!memberUserId) continue;
                            try {
                                // eslint-disable-next-line no-await-in-loop
                                const { uuid, name } = await fetchUserCanonical(memberUserId);
                                if (uuid) collected.push({ uuid, name });
                            } catch {
                                continue;
                            }
                        }
                    }
                    const next = res?.data?.paging?.nextPage;
                    url = next ? `${apiBase()}${next.startsWith("/") ? "" : "/"}${next}` : "";
                }
                const byUuid = new Map();
                for (const s of collected) byUuid.set(s.uuid, s);
                const unique = Array.from(byUuid.values()).sort((a, b) => a.name.localeCompare(b.name));
                setStudents(unique);
            } catch (e) {
                setStudentsError(e?.response?.data?.message || e?.message || "Failed to load enrollments.");
            } finally {
                setLoadingStudents(false);
            }
        };
        fetchAllStudents();
    }, [platformUrl, courseIdParam, token, enrollmentRole]);

    const resolveSelfUuid = async () => {
        const { uuid } = await fetchUserCanonical(userIdParam);
        return uuid || userIdParam;
    };

    const handleSend = async () => {
        if (!userIdParam) {
            setError("Missing uuid query parameter.");
            return;
        }
        if (!lineItemUrl || !backEndUrl) {
            setError("Missing lineItemUrl or backEndUrl prop.");
            return;
        }
        try {
            setSending(true);
            setError("");
            setResult(null);
            const selfUuid = await resolveSelfUuid();
            const payload = { url: lineItemUrl, userId: selfUuid, courseId: courseIdParam, score: 87 };
            const res = await axios.post(`${backEndUrl}/lti/grades`, payload, {
                headers: { "Content-Type": "application/json" },
            });
            setResult(res.data ?? { ok: true });
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to post score.");
        } finally {
            setSending(false);
        }
    };

    const postFilledGrades = async () => {
        if (!lineItemUrl || !backEndUrl) {
            setError("Missing lineItemUrl or backEndUrl prop.");
            return;
        }
        setError("");
        setResult(null);
        const entries = Object.entries(grades)
            .filter(([, val]) => val !== "" && val !== undefined && val !== null)
            .map(([uuid, val]) => ({ uuid, score: Number(val) }));
        if (entries.length === 0) {
            setError("No grades entered.");
            return;
        }
        const invalid = entries.find((e) => Number.isNaN(e.score) || e.score < 0 || e.score > 100);
        if (invalid) {
            setError("All entered grades must be numbers between 0 and 100.");
            return;
        }
        setSending(true);
        setRowStatuses({});
        try {
            const results = await Promise.allSettled(
                entries.map(({ uuid, score }) =>
                    axios.post(
                        `${backEndUrl}/lti/grades`,
                        { url: lineItemUrl, userId: uuid, courseId: courseIdParam, score },
                        { headers: { "Content-Type": "application/json" } }
                    )
                )
            );
            const responses = [];
            const newStatuses = {};
            entries.forEach(({ uuid }, i) => {
                const r = results[i];
                if (r.status === "fulfilled") {
                    newStatuses[uuid] = { status: "ok", message: "Posted" };
                    responses.push({ uuid, response: r.value.data || "ok" });
                } else {
                    const err = r.reason;
                    newStatuses[uuid] = { status: "error", message: err?.message || "Failed" };
                    responses.push({ uuid, error: err?.message || "Failed" });
                }
            });
            setRowStatuses(newStatuses);
            setResult(responses);
        } catch (e) {
            setError(e?.message || "Failed to post one or more grades.");
        } finally {
            setSending(false);
        }
    };

    const isStudent = (enrollmentRole || "").toLowerCase() === "student";
    const isInstructor = (enrollmentRole || "").toLowerCase() === "instructor";

    return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
            <h1>Welcome to the AI Generated LTI Assignment!</h1>
            <p>
                {isInstructor
                    ? "Enter grades for students below and post them to your backend."
                    : "This will send a hardcoded score of 87 to your backend."}
            </p>

            <div
                style={{
                    margin: "12px auto 0",
                    padding: 12,
                    maxWidth: 1000,
                    textAlign: "left",
                    background: "#f6f8fa",
                    borderRadius: 8,
                    fontSize: 14,
                }}
            >
                <div>
                    <strong>Enrollment Role:</strong>{" "}
                    {loadingEnrollment ? "Loading…" : enrollmentRole || <em>Unknown</em>}
                </div>
                {enrollmentError && (
                    <div style={{ marginTop: 6, color: "#b00020" }}>
                        <strong>Enrollment Error:</strong> {enrollmentError}
                    </div>
                )}
            </div>

            {isInstructor && (
                <div style={{ marginTop: 20, padding: "0 16px" }}>
                    <div style={{ textAlign: "left", margin: "0 auto", maxWidth: 1000 }}>
                        <h3>Students</h3>
                        {loadingStudents && <p>Loading students…</p>}
                        {studentsError && <p style={{ color: "#b00020" }}><strong>Error:</strong> {studentsError}</p>}
                        {!loadingStudents && !studentsError && students.length === 0 && <p>No student enrollments found.</p>}
                        {students.length > 0 && (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8 }}>
                                        <thead>
                                        <tr>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Name</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>UUID</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Grade</th>
                                            <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid #ddd" }}>Status</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {students.map((s) => (
                                            <tr key={s.uuid}>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>{s.name}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0", fontFamily: "monospace" }}>{s.uuid}</td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        step="1"
                                                        value={grades[s.uuid] ?? ""}
                                                        onChange={(e) => setGrades((prev) => ({ ...prev, [s.uuid]: e.target.value }))}
                                                        placeholder="—"
                                                        style={{ width: 120, padding: "6px 8px", borderRadius: 6, border: "1px solid #ccc" }}
                                                        disabled={sending}
                                                    />
                                                </td>
                                                <td style={{ padding: 8, borderBottom: "1px solid #f0f0f0" }}>
                                                    {rowStatuses[s.uuid]?.status === "ok" && <span style={{ color: "green" }}>Posted</span>}
                                                    {rowStatuses[s.uuid]?.status === "error" && <span style={{ color: "#b00020" }}>{rowStatuses[s.uuid]?.message}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ textAlign: "right", marginTop: 12 }}>
                                    <button
                                        onClick={postFilledGrades}
                                        disabled={sending}
                                        style={{ padding: "10px 16px", fontSize: 16, borderRadius: 8, cursor: sending ? "not-allowed" : "pointer" }}
                                    >
                                        {sending ? "Posting…" : "Post Selected Grades"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {!isInstructor && !isStudent && (
                <button
                    onClick={handleSend}
                    disabled={sending}
                    style={{ marginTop: "20px", padding: "10px 20px", fontSize: 16, borderRadius: 8, cursor: sending ? "not-allowed" : "pointer" }}
                >
                    {sending ? "Sending…" : "Send Score"}
                </button>
            )}

            {isStudent && <p style={{ marginTop: 16 }}>This action is available only to non-student roles for this course.</p>}

            {error && (
                <p style={{ marginTop: "20px", color: "red" }}>
                    <strong>Error:</strong> {error}
                </p>
            )}

            {result && (
                <div style={{ marginTop: "20px", textAlign: "left", maxWidth: "1000px", marginLeft: "auto", marginRight: "auto" }}>
                    <strong>Responses</strong>
                    <pre
                        style={{
                            marginTop: "10px",
                            background: "#f6f8fa",
                            padding: "12px",
                            borderRadius: "6px",
                            maxHeight: "500px",
                            overflowX: "auto",
                            overflowY: "auto",
                            fontSize: "13px",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
{JSON.stringify(result, null, 2)}
          </pre>
                </div>
            )}
        </div>
    );
};

export default LtiAssignment;
